import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { ProjectContextPanel } from '../components/ProjectContextPanel';
import { sendMessageStream, StreamChunk, IntentResponse, ExecutionMode } from '../services/api';
import { listTasks, createTask, updateTask, Task } from '../services/tasksApi';
import { getUserApps, UserApp } from '../services/appRegistry';
import { Settings } from 'lucide-react';
import {
  SpaceBackground,
  Header,
  ChatBar,
  BottomNav,
  Sidebar,
  SettingsPanel,
  PageTitle,
  GlassCard,
  AtomIcon,
  Plus,
  Briefcase,
  ChevronLeft,
  X,
  Upload,
  Link,
  FileText,
  Edit,
  ArrowUp,
  MoreVertical,
  Trash2,
  Mic,
  Radio,
  Ghost,
  Zap,
  Cpu,
  Activity
} from '../components/shared';

// Project interface moved to after imports (with API integration)

// Plus menu for adding knowledge in chat
const PlusMenu = ({
  isOpen,
  onClose,
  onAddFile,
  onAddLink,
  onEditSettings
}: {
  isOpen: boolean;
  onClose: () => void;
  onAddFile: () => void;
  onAddLink: () => void;
  onEditSettings: () => void;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute bottom-16 left-0 w-56 bg-black/95 backdrop-blur-xl border border-cyan-400/30 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(34,211,238,0.3)] z-50"
    >
      <button
        onClick={() => { onAddFile(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all"
      >
        <Upload className="w-4 h-4" />
        <span>Upload files, photos, videos</span>
      </button>
      <button
        onClick={() => { onAddLink(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all border-t border-cyan-400/10"
      >
        <Link className="w-4 h-4" />
        <span>Add link from internet</span>
      </button>
      <button
        onClick={() => { onEditSettings(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all border-t border-cyan-400/10"
      >
        <Edit className="w-4 h-4" />
        <span>Edit project settings</span>
      </button>
    </div>
  );
};

// Link input modal
const LinkInputModal = ({
  isOpen,
  onClose,
  onAdd
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (url: string) => void;
}) => {
  const [url, setUrl] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 bg-black/95 border border-cyan-400/30 rounded-2xl p-6 shadow-[0_0_50px_rgba(34,211,238,0.3)]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4">Add Link</h3>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="w-full bg-cyan-400/5 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 outline-none focus:border-cyan-400/60 mb-4"
        />
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-cyan-400/70 hover:text-cyan-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { onAdd(url); setUrl(''); onClose(); }}
            disabled={!url.trim()}
            className={`px-4 py-2 rounded-xl font-semibold transition-all ${url.trim() ? 'bg-cyan-400 text-black' : 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'}`}
          >
            Add Link
          </button>
        </div>
      </div>
    </div>
  );
};

// Action Modal for project actions
const ActionModal = ({
  isOpen,
  onClose,
  project,
  onUpdateProject,
  onDelete
}: {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onUpdateProject: () => void;
  onDelete: () => void;
}) => {
  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative z-10 w-full max-w-sm mx-4 bg-black/95 backdrop-blur-2xl border border-cyan-400/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)]">
        <div className="p-4 border-b border-cyan-400/20">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${project.color} flex items-center justify-center`}>
              <span className="text-xl">{project.icon}</span>
            </div>
            <div>
              <h3 className="text-cyan-300 font-semibold text-lg">{project.name}</h3>
              <p className="text-cyan-400/50 text-sm mt-0.5">{project.tasksCount} tasks</p>
            </div>
          </div>
        </div>

        <div className="p-2">
          <button
            onClick={() => { onUpdateProject(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-4 rounded-xl transition-all"
          >
            <Edit className="w-5 h-5" />
            <span>Update project</span>
          </button>
          <button
            onClick={() => { onDelete(); onClose(); }}
            className="w-full text-left px-4 py-4 text-base text-red-400/90 hover:bg-red-400/10 hover:text-red-300 flex items-center space-x-4 rounded-xl transition-all border-t border-cyan-400/10 mt-2"
          >
            <Trash2 className="w-5 h-5" />
            <span>Delete project</span>
          </button>
        </div>

        <div className="p-4 border-t border-cyan-400/20">
          <button
            onClick={onClose}
            className="w-full py-3 text-cyan-400 font-semibold bg-cyan-400/10 hover:bg-cyan-400/20 rounded-xl transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Project Button - matching MyAppsScreen style
const CreateProjectButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="group flex flex-col items-center space-y-3 p-4 rounded-2xl bg-cyan-500/[0.03] hover:bg-cyan-400/[0.1] backdrop-blur-xl border border-dashed border-cyan-400/30 hover:border-cyan-400/60 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(34,211,238,0.3)]"
  >
    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center group-hover:bg-cyan-400/20 group-hover:border-cyan-400/50 transition-all">
      <Plus className="w-6 h-6 md:w-8 md:h-8 text-cyan-400/60 group-hover:text-cyan-300" />
    </div>
    <span className="text-cyan-400/60 text-xs font-semibold text-center group-hover:text-cyan-300 transition-colors">
      New Project
    </span>
  </button>
);

// Project Card - matching MyAppsScreen style
const ProjectCard = ({ project, onClick, onMenuOpen }: { project: Project; onClick: () => void; onMenuOpen: () => void }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      className="w-full flex flex-col items-center space-y-3 p-4 rounded-2xl bg-cyan-500/[0.03] hover:bg-cyan-400/[0.1] backdrop-blur-xl border border-cyan-400/20 hover:border-cyan-400/50 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(34,211,238,0.3)]"
    >
      <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${project.color} flex items-center justify-center shadow-lg group-hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]`}>
        <span className="text-xl md:text-2xl">{project.icon}</span>
      </div>
      <span className="text-cyan-400/80 text-xs font-semibold text-center group-hover:text-cyan-300 transition-colors line-clamp-2">
        {project.name}
      </span>
      <span className="text-cyan-400/50 text-[10px]">{project.tasksCount} tasks</span>
    </button>

    <button
      onClick={(e) => { e.stopPropagation(); onMenuOpen(); }}
      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-cyan-400/30 opacity-0 group-hover:opacity-100 hover:bg-cyan-400/20 transition-all md:opacity-0 md:group-hover:opacity-100"
    >
      <MoreVertical className="w-4 h-4 text-cyan-400" />
    </button>
  </div>
);

// New Project Form Component - Split Screen with Chat Preview (Responsive)
const NewProjectForm = ({ onClose, onCreate }: { onClose: () => void; onCreate: (project: Partial<Project>, connectedTasks?: Task[], connectedApps?: ProjectApp[], pendingFileUploads?: File[]) => void }) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [showTemplates, setShowTemplates] = useState(true);  // Start with template selection
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'work' | 'life' | 'creative' | 'learning'>('all');
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [goal, setGoal] = useState('');
  const [projectIcon, setProjectIcon] = useState('📁');
  const [projectColor, setProjectColor] = useState('from-cyan-400 to-blue-400');
  const [files, setFiles] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [newLink, setNewLink] = useState('');
  const [previewInputText, setPreviewInputText] = useState('');
  const [previewConversation, setPreviewConversation] = useState<{ role: string; message: string }[]>([]);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Task & App picker state
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);
  const [appPickerOpen, setAppPickerOpen] = useState(false);
  const [connectedTasks, setConnectedTasks] = useState<Task[]>([]);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [connectedApps, setConnectedApps] = useState<ProjectApp[]>([]);
  const [availableUserApps, setAvailableUserApps] = useState<UserApp[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  // Apply template to form
  const applyTemplate = (template: ProjectTemplate) => {
    setProjectName(template.name);
    setDescription(template.description);
    setInstructions(template.suggestedInstructions);
    setGoal(template.suggestedGoal);
    setProjectIcon(template.icon);
    setProjectColor(template.color);
    setShowTemplates(false);
  };

  // Filter templates by category
  const filteredTemplates = selectedCategory === 'all'
    ? PROJECT_TEMPLATES
    : PROJECT_TEMPLATES.filter(t => t.category === selectedCategory);

  const handleAddLink = () => {
    if (newLink.trim()) {
      setLinks([...links, newLink.trim()]);
      setNewLink('');
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList) {
      Array.from(fileList).forEach(file => {
        setFiles(prev => [...prev, file.name]);
        setPendingFiles(prev => [...prev, file]);
      });
    }
  };

  const handleCreate = () => {
    if (projectName.trim()) {
      onCreate({
        name: projectName,
        description,
        instructions,
        goal,
        icon: projectIcon,
        color: projectColor,
        tasksCount: connectedTasks.length,
        files,
        links,
        connected_apps: connectedApps.length > 0 ? connectedApps : undefined
      }, connectedTasks.length > 0 ? connectedTasks : undefined, connectedApps.length > 0 ? connectedApps : undefined, pendingFiles.length > 0 ? pendingFiles : undefined);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handlePreviewSend = () => {
    if (!previewInputText.trim()) return;

    const userMessage = previewInputText.trim();
    setPreviewConversation(prev => [...prev, { role: 'user', message: userMessage }]);
    setPreviewInputText('');

    // Simulate AI response
    setTimeout(() => {
      setPreviewConversation(prev => [...prev, {
        role: 'assistant',
        message: `I'll help you with "${projectName || 'your project'}". ${userMessage.includes('?') ? 'Based on your question, here\'s what I can tell you...' : 'I understand. Let me work on that for you...'}`
      }]);
    }, 1000);
  };

  const canCreate = projectName.trim().length > 0;

  // Preview Panel with Chat (for desktop and mobile modal)
  const PreviewPanel = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className={`${isMobile ? 'h-full' : 'w-full h-full'} flex flex-col bg-gradient-to-br from-black to-cyan-950/20`}>
      {/* Preview Header */}
      <header className="p-4 flex items-center justify-between border-b border-cyan-400/10">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${projectColor} flex items-center justify-center`}>
            <span className="text-lg">{projectIcon}</span>
          </div>
          <h2 className="text-lg font-semibold text-cyan-400/80">{projectName || 'Your Project'}</h2>
        </div>
        <div className="flex items-center space-x-2">
          {isMobile && (
            <button
              onClick={() => setShowMobilePreview(false)}
              className="p-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 mr-2"
            >
              <X className="w-4 h-4 text-cyan-400" />
            </button>
          )}
          <span className="text-cyan-400/50 text-xs hidden sm:inline">Chat with your project</span>
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
        </div>
      </header>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="space-y-4">
          {/* Welcome message */}
          {previewConversation.length === 0 && (
            <div className="flex justify-start">
              <div className="w-8 h-8 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mr-3 shrink-0">
                <AtomIcon className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="max-w-[80%] p-4 rounded-2xl bg-cyan-500/[0.05] border border-cyan-400/20">
                <p className="text-cyan-300 text-sm leading-relaxed">
                  {projectName
                    ? `Welcome to "${projectName}"! I'm ready to help you with this project. What would you like to work on?`
                    : 'Name your project to start chatting with it. I\'ll help you accomplish your goals.'}
                </p>
              </div>
            </div>
          )}

          {/* Conversation */}
          {previewConversation.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mr-3 shrink-0">
                  <AtomIcon className="w-5 h-5 text-cyan-400" />
                </div>
              )}
              <div className={`max-w-[80%] p-4 rounded-2xl ${msg.role === 'user'
                  ? 'bg-cyan-400/20 border border-cyan-400/40'
                  : 'bg-cyan-500/[0.05] border border-cyan-400/20'
                }`}>
                <p className="text-cyan-300 text-sm leading-relaxed">{msg.message}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Input */}
      <div className="p-4 border-t border-cyan-400/10">
        <GlassCard className="flex items-center p-2 pr-4">
          <button className="p-3 rounded-full mr-3 bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400 transition-all border border-cyan-400/40">
            <Plus className="w-5 h-5" />
          </button>

          <input
            type="text"
            value={previewInputText}
            onChange={(e) => setPreviewInputText(e.target.value)}
            placeholder={projectName ? `Chat with ${projectName}...` : "Name your project first..."}
            disabled={!projectName}
            className="flex-1 bg-transparent border-none outline-none text-cyan-300 placeholder-cyan-400/40 text-sm md:text-base px-2 font-medium disabled:opacity-50"
            onKeyPress={(e) => e.key === 'Enter' && handlePreviewSend()}
          />

          <button
            onClick={handlePreviewSend}
            disabled={!previewInputText.trim() || !projectName}
            className={`p-2.5 rounded-full transition-all duration-300 border ${previewInputText.trim() && projectName ? 'bg-cyan-400 text-black border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.6)] hover:shadow-[0_0_35px_rgba(34,211,238,0.8)] hover:scale-105' : 'bg-cyan-400/10 text-cyan-400/40 border-cyan-400/20 cursor-not-allowed'}`}
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </GlassCard>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col lg:flex-row h-full">
        {/* Left Side - Form (full width on mobile, half on desktop) */}
        <div className="flex-1 lg:w-1/2 flex flex-col lg:border-r border-cyan-400/20">
          {/* Header */}
          <header className="p-4 md:p-6 flex items-center border-b border-cyan-400/10">
            <button
              onClick={onClose}
              className="p-2 md:p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-4 md:mr-6"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
            </button>

            <button onClick={handleLogoClick} className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity">
              <AtomIcon className="w-8 h-8 md:w-10 md:h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
              <h1 className="text-xl md:text-2xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans">
                New Project
              </h1>
            </button>

            <button onClick={handleLogoClick} className="ml-auto hover:scale-105 transition-transform hidden md:block">
              <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
            </button>
          </header>

          {/* Form Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Template Selection Screen */}
            {showTemplates ? (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-cyan-300 mb-2">Start with a Template</h2>
                  <p className="text-cyan-400/60 text-sm">Choose a template to get started quickly, or create from scratch</p>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {[
                    { key: 'all', label: 'All', icon: '✨' },
                    { key: 'work', label: 'Work', icon: '💼' },
                    { key: 'life', label: 'Life', icon: '🏠' },
                    { key: 'creative', label: 'Creative', icon: '🎨' },
                    { key: 'learning', label: 'Learning', icon: '📚' }
                  ].map(cat => (
                    <button
                      key={cat.key}
                      onClick={() => setSelectedCategory(cat.key as typeof selectedCategory)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedCategory === cat.key
                          ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/50'
                          : 'bg-cyan-400/5 text-cyan-400/60 border border-cyan-400/20 hover:border-cyan-400/40'
                        }`}
                    >
                      <span className="mr-1">{cat.icon}</span> {cat.label}
                    </button>
                  ))}
                </div>

                {/* Template Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredTemplates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template)}
                      className="text-left p-4 rounded-xl bg-cyan-400/5 border border-cyan-400/20 hover:border-cyan-400/40 hover:bg-cyan-400/10 transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                          <span className="text-lg">{template.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-cyan-300 font-semibold text-sm mb-1">{template.name}</h3>
                          <p className="text-cyan-400/50 text-xs line-clamp-2">{template.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Start from Scratch */}
                <div className="pt-4 border-t border-cyan-400/10">
                  <button
                    onClick={() => setShowTemplates(false)}
                    className="w-full p-4 rounded-xl bg-cyan-400/5 border border-dashed border-cyan-400/30 hover:border-cyan-400/50 hover:bg-cyan-400/10 transition-all text-center"
                  >
                    <span className="text-cyan-400/70 text-sm">✏️ Start from scratch</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-5 md:space-y-6">
                {/* Back to Templates Button */}
                <button
                  onClick={() => setShowTemplates(true)}
                  className="text-cyan-400/60 hover:text-cyan-400 text-sm flex items-center gap-1 mb-2"
                >
                  ← Back to templates
                </button>

                {/* Name */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Name your project</label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Enter project name..."
                    className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 transition-all text-base"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Add a short description about your project and what it does</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your project..."
                    rows={3}
                    className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 transition-all resize-none text-base"
                  />
                </div>

                {/* Instructions */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Instructions</label>
                  <p className="text-cyan-400/50 text-xs mb-2">What does this project do? How does your project behave? What should the project avoid doing?</p>
                  <textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Enter detailed instructions..."
                    rows={4}
                    className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 transition-all resize-none text-base"
                  />
                </div>

                {/* Knowledge */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Knowledge</label>
                  <p className="text-cyan-400/50 text-xs mb-3">Upload files, images, videos, or add links as reference material.</p>

                  {/* File Upload + Add/Connect Tasks + Add/Connect Apps */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      onClick={handleFileUpload}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl hover:bg-cyan-400/10 hover:border-cyan-400/50 transition-all text-sm"
                    >
                      <Upload className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400/80">Upload files</span>
                    </button>
                    <button
                      onClick={() => {
                        setTaskPickerOpen(true);
                        if (user?.uid && availableTasks.length === 0) {
                          listTasks({ user_id: user.uid, status: 'all', limit: 50 }).then(res => {
                            if (res.success) setAvailableTasks(res.tasks);
                          }).catch(() => {});
                        }
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl hover:bg-cyan-400/10 hover:border-cyan-400/50 transition-all text-sm"
                    >
                      <Briefcase className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400/80">Add/Connect Tasks</span>
                    </button>
                    <button
                      onClick={() => {
                        setAppPickerOpen(true);
                        if (user?.uid && availableUserApps.length === 0) {
                          getUserApps(user.uid).then(apps => setAvailableUserApps(apps)).catch(() => {});
                        }
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl hover:bg-cyan-400/10 hover:border-cyan-400/50 transition-all text-sm"
                    >
                      <Cpu className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400/80">Add/Connect Apps</span>
                    </button>
                  </div>

                  {/* Connected Tasks chips */}
                  {connectedTasks.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {connectedTasks.map(task => (
                        <div key={task.task_id} className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                          <Briefcase className="w-4 h-4 text-cyan-400" />
                          <span className="text-cyan-400/80 text-sm truncate max-w-[150px]">{task.title}</span>
                          <button onClick={() => setConnectedTasks(prev => prev.filter(t => t.task_id !== task.task_id))} className="text-cyan-400/50 hover:text-cyan-300">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Connected Apps chips */}
                  {connectedApps.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {connectedApps.map(app => (
                        <div key={app.provider} className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                          <span className="text-sm">{app.icon}</span>
                          <span className="text-cyan-400/80 text-sm truncate max-w-[150px]">{app.name}</span>
                          <button onClick={() => setConnectedApps(prev => prev.filter(a => a.provider !== app.provider))} className="text-cyan-400/50 hover:text-cyan-300">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Uploaded Files */}
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {files.map((file, i) => (
                        <div key={i} className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                          <FileText className="w-4 h-4 text-cyan-400" />
                          <span className="text-cyan-400/80 text-sm truncate max-w-[150px]">{file}</span>
                          <button onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-cyan-400/50 hover:text-cyan-300">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Link */}
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      placeholder="Add a link..."
                      className="flex-1 bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-2 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
                    />
                    <button
                      onClick={handleAddLink}
                      className="px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 rounded-xl hover:bg-cyan-400/20 transition-all"
                    >
                      <Link className="w-4 h-4 text-cyan-400" />
                    </button>
                  </div>

                  {/* Added Links */}
                  {links.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {links.map((link, i) => (
                        <div key={i} className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/30 rounded-lg max-w-full">
                          <Link className="w-4 h-4 text-cyan-400 shrink-0" />
                          <span className="text-cyan-400/80 text-sm truncate">{link}</span>
                          <button onClick={() => setLinks(links.filter((_, idx) => idx !== i))} className="text-cyan-400/50 hover:text-cyan-300 shrink-0">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Goal / Purpose */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Goal / Purpose of the Project</label>
                  <textarea
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="What is the overall goal or purpose of this project?"
                    rows={3}
                    className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 transition-all resize-none text-base"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="p-4 md:p-6 border-t border-cyan-400/10 bg-black/50 backdrop-blur-xl">
            {/* Mobile: Preview Chat Button */}
            <button
              onClick={() => setShowMobilePreview(true)}
              className="w-full py-3 mb-3 rounded-xl font-semibold text-base transition-all bg-cyan-400/10 text-cyan-400 border border-cyan-400/30 hover:bg-cyan-400/20 lg:hidden"
            >
              Preview Chat
            </button>

            {/* Create Button */}
            <button
              onClick={handleCreate}
              disabled={!canCreate}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${canCreate
                  ? 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:shadow-[0_0_40px_rgba(34,211,238,0.7)]'
                  : 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'
                }`}
            >
              Create Project
            </button>
          </div>
        </div>

        {/* Right Side - Chat Preview (hidden on mobile, shown on lg+) */}
        <div className="hidden lg:flex lg:w-1/2">
          <PreviewPanel />
        </div>
      </div>

      {/* Mobile Preview Modal */}
      {showMobilePreview && (
        <div className="fixed inset-0 z-[80] bg-black lg:hidden">
          <PreviewPanel isMobile />
        </div>
      )}

      {/* Task Picker Modal */}
      {taskPickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-black/95 border border-cyan-400/30 rounded-2xl w-full max-w-md max-h-[70vh] overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)]">
            <div className="p-4 border-b border-cyan-400/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-cyan-300">Add/Connect Tasks</h3>
              <button onClick={() => setTaskPickerOpen(false)} className="p-2 rounded-full hover:bg-cyan-400/10 transition-colors">
                <X className="w-5 h-5 text-cyan-400/60" />
              </button>
            </div>

            {/* Create new task */}
            <div className="p-4 border-b border-cyan-400/10">
              <p className="text-cyan-400/50 text-xs mb-2">Create a new task for this project</p>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Task name..."
                  className="flex-1 bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-2 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newTaskTitle.trim() && user?.uid) {
                      setIsCreatingTask(true);
                      createTask({ user_id: user.uid, title: newTaskTitle.trim() }).then(res => {
                        if (res.success && res.task) {
                          setConnectedTasks(prev => [...prev, res.task]);
                          setNewTaskTitle('');
                        }
                      }).catch(() => {}).finally(() => setIsCreatingTask(false));
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (newTaskTitle.trim() && user?.uid) {
                      setIsCreatingTask(true);
                      createTask({ user_id: user.uid, title: newTaskTitle.trim() }).then(res => {
                        if (res.success && res.task) {
                          setConnectedTasks(prev => [...prev, res.task]);
                          setNewTaskTitle('');
                        }
                      }).catch(() => {}).finally(() => setIsCreatingTask(false));
                    }
                  }}
                  disabled={!newTaskTitle.trim() || isCreatingTask}
                  className="px-4 py-2 bg-cyan-400/20 border border-cyan-400/30 rounded-xl hover:bg-cyan-400/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingTask ? (
                    <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 text-cyan-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Existing tasks */}
            <div className="p-4 overflow-y-auto max-h-[40vh] space-y-2">
              <p className="text-cyan-400/50 text-xs mb-2">Or connect an existing task</p>
              {availableTasks
                .filter(t => !connectedTasks.some(ct => ct.task_id === t.task_id))
                .map(task => (
                  <button
                    key={task.task_id}
                    onClick={() => {
                      setConnectedTasks(prev => [...prev, task]);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-cyan-400/5 hover:bg-cyan-400/10 border border-cyan-400/10 hover:border-cyan-400/30 transition-all"
                  >
                    <Briefcase className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span className="text-cyan-300 font-medium text-sm text-left truncate">{task.title}</span>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                      task.status === 'running' ? 'bg-green-400/20 text-green-400' :
                      task.status === 'completed' ? 'bg-cyan-400/20 text-cyan-400' :
                      task.status === 'paused' ? 'bg-orange-400/20 text-orange-400' :
                      'bg-yellow-400/20 text-yellow-400'
                    }`}>{task.status}</span>
                  </button>
                ))}
              {availableTasks.filter(t => !connectedTasks.some(ct => ct.task_id === t.task_id)).length === 0 && (
                <p className="text-cyan-400/50 text-center py-4 text-sm">
                  {availableTasks.length === 0 ? 'No tasks found' : 'All tasks connected'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* App Picker Modal */}
      {appPickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-black/95 border border-cyan-400/30 rounded-2xl w-full max-w-md max-h-[70vh] overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)]">
            <div className="p-4 border-b border-cyan-400/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-cyan-300">Add/Connect Apps</h3>
              <button onClick={() => setAppPickerOpen(false)} className="p-2 rounded-full hover:bg-cyan-400/10 transition-colors">
                <X className="w-5 h-5 text-cyan-400/60" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[55vh] space-y-4">
              {/* User-created apps */}
              {availableUserApps.length > 0 && (
                <div>
                  <p className="text-cyan-400/50 text-xs mb-2 uppercase tracking-wider font-semibold">My Apps</p>
                  <div className="space-y-2">
                    {availableUserApps
                      .filter(app => !connectedApps.some(ca => ca.provider === app.id))
                      .map(app => (
                        <button
                          key={app.id}
                          onClick={() => {
                            setConnectedApps(prev => [...prev, {
                              provider: app.id,
                              name: app.name,
                              icon: '📱',
                              connected_at: new Date().toISOString()
                            }]);
                          }}
                          className="w-full flex items-center gap-3 p-3 rounded-xl bg-cyan-400/5 hover:bg-cyan-400/10 border border-cyan-400/10 hover:border-cyan-400/30 transition-all"
                        >
                          <Cpu className="w-4 h-4 text-cyan-400" />
                          <span className="text-cyan-300 font-medium text-sm">{app.name}</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Integration apps */}
              <div>
                <p className="text-cyan-400/50 text-xs mb-2 uppercase tracking-wider font-semibold">Integration Apps</p>
                <div className="space-y-2">
                  {AVAILABLE_APPS
                    .filter(app => !connectedApps.some(ca => ca.provider === app.provider))
                    .map(app => (
                      <button
                        key={app.provider}
                        onClick={() => {
                          setConnectedApps(prev => [...prev, {
                            ...app,
                            connected_at: new Date().toISOString()
                          }]);
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-cyan-400/5 hover:bg-cyan-400/10 border border-cyan-400/10 hover:border-cyan-400/30 transition-all"
                      >
                        <span className="text-xl">{app.icon}</span>
                        <span className="text-cyan-300 font-medium text-sm">{app.name}</span>
                      </button>
                    ))}
                  {AVAILABLE_APPS.filter(app => !connectedApps.some(ca => ca.provider === app.provider)).length === 0 && (
                    <p className="text-cyan-400/50 text-center py-4 text-sm">All apps connected</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf,.doc,.docx,.txt"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

// Project Chat View - Full screen chat with project (same style as HomeScreen)
const ProjectChatView = ({
  project,
  onClose,
  onUpdateProject
}: {
  project: Project;
  onClose: () => void;
  onUpdateProject: (updates: Partial<Project>) => void;
}) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contextPanelOpen, setContextPanelOpen] = useState(true);
  const [conversation, setConversation] = useState<{ role: string; message: string }[]>([
    { role: 'assistant', message: `Welcome to "${project.name}"! I'm ready to help you with this project. I have access to your project's knowledge base and instructions. What would you like to work on?` }
  ]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [connectedApps, setConnectedApps] = useState<ProjectApp[]>(project.connected_apps || []);
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [appConnectError, setAppConnectError] = useState<string | null>(null);
  const [isConnectingApp, setIsConnectingApp] = useState(false);

  // Editable settings state
  const [editName, setEditName] = useState(project.name);
  const [editDescription, setEditDescription] = useState(project.description || '');
  const [editInstructions, setEditInstructions] = useState(project.instructions || '');
  const [editGoal, setEditGoal] = useState(project.goal || '');
  const [files, setFiles] = useState<string[]>(project.files || []);
  const [links, setLinks] = useState<string[]>(project.links || []);
  const [newLink, setNewLink] = useState('');
  const [settingsSavedAt, setSettingsSavedAt] = useState<Date | null>(null);
  const lastSavedSettingsRef = useRef({ name: project.name, description: project.description || '', instructions: project.instructions || '', goal: project.goal || '' });

  const chatScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const modeMenuRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // Load conversation history from Firestore on mount
  useEffect(() => {
    if (!user?.uid || !project?.id || historyLoaded) return;

    const loadHistory = async () => {
      try {
        const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
        const { db } = await import('../services/firebase');

        const projectId = project.project_id || String(project.id);
        const historyRef = collection(db, 'conversation_history', user.uid, 'interactions');
        const q = query(historyRef, orderBy('timestamp', 'desc'), limit(50));
        const snapshot = await getDocs(q);

        const history: { role: string; message: string }[] = [];
        snapshot.docs.reverse().forEach(doc => {
          const data = doc.data();
          if (data.project_id === projectId) {
            if (data.utterance) {
              history.push({ role: 'user', message: data.utterance });
            }
            if (data.response_summary) {
              history.push({
                role: 'assistant',
                message: typeof data.response_summary === 'string'
                  ? data.response_summary
                  : JSON.stringify(data.response_summary)
              });
            }
          }
        });

        if (history.length > 0) {
          setConversation(prev => [...prev, ...history]);
          console.log(`[ProjectChat] Loaded ${history.length} messages from history`);
        }
        setHistoryLoaded(true);
      } catch (error) {
        console.error('[ProjectChat] Error loading history:', error);
        setHistoryLoaded(true);
      }
    };

    loadHistory();
  }, [user?.uid, project?.id, project?.project_id, historyLoaded]);

  // Fetch tasks for this project
  useEffect(() => {
    const fetchProjectTasks = async () => {
      if (!user?.uid) return;
      try {
        const response = await listTasks({
          user_id: user.uid,
          project_id: project.project_id || String(project.id),
          limit: 10
        });
        if (response.success && response.tasks) {
          setProjectTasks(response.tasks as unknown as Task[]);
        }
      } catch (error) {
        console.error('[ProjectChat] Error fetching tasks:', error);
      }
    };
    fetchProjectTasks();
  }, [user?.uid, project.project_id, project.id]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(event.target as Node)) {
        setPlusMenuOpen(false);
      }
      if (modeMenuRef.current && !modeMenuRef.current.contains(event.target as Node)) {
        setModeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-save project settings every 30 seconds when dirty
  useEffect(() => {
    if (!showSettings) return;
    const interval = setInterval(() => {
      const current = { name: editName, description: editDescription, instructions: editInstructions, goal: editGoal };
      const last = lastSavedSettingsRef.current;
      const hasChanges =
        current.name !== last.name ||
        current.description !== last.description ||
        current.instructions !== last.instructions ||
        current.goal !== last.goal;

      if (!hasChanges) return;

      onUpdateProject({
        name: editName,
        description: editDescription,
        instructions: editInstructions,
        goal: editGoal,
        files,
        links
      });
      lastSavedSettingsRef.current = { ...current };
      setSettingsSavedAt(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [showSettings, editName, editDescription, editInstructions, editGoal, files, links, onUpdateProject]);

  // Map UI mode labels to API execution mode values
  const getExecutionMode = (mode: string): ExecutionMode => {
    switch (mode) {
      case 'Fast': return 'fast';
      case 'Super': return 'super';
      default: return 'super';
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText.trim();
    setConversation(prev => [...prev, { role: 'user', message: userMessage }]);
    setInputText('');
    setIsLoading(true);
    setStreamingContent('');

    // Build conversation history for context
    const history = conversation.map(msg => ({
      role: msg.role as 'user' | 'assistant',
      content: msg.message
    }));

    let fullResponse = '';

    await sendMessageStream(
      userMessage,
      {
        executionMode: getExecutionMode(chatMode),
        projectId: project.project_id || String(project.id),
        projectName: project.name || '',
        projectKnowledge: [
          ...(project.links || []),
          ...(project.files || []),
        ].filter(Boolean),
      },
      history,
      // onChunk
      (chunk: StreamChunk) => {
        if (chunk.type === 'content') {
          fullResponse += chunk.data;
          setStreamingContent(fullResponse);
        }
      },
      // onComplete
      (result: IntentResponse) => {
        const assistantContent = result.result as string || fullResponse || 'I apologize, but I could not generate a response.';
        setConversation(prev => [...prev, { role: 'assistant', message: assistantContent }]);
        setStreamingContent('');
        setIsLoading(false);
      },
      // onError
      (error: Error) => {
        console.error('[ProjectChat] Error:', error);
        setConversation(prev => [...prev, {
          role: 'assistant',
          message: `Error: ${error.message || 'Something went wrong. Please try again.'}`
        }]);
        setStreamingContent('');
        setIsLoading(false);
      }
    );
  };

  const handleAddFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || !user?.uid) return;

    const projectId = project.project_id || String(project.id);

    for (const file of Array.from(fileList)) {
      try {
        const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
        const { storage } = await import('../services/firebase');
        const { addFileToProject } = await import('../services/projectsApi');

        const storagePath = `projects/${user.uid}/${projectId}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        await addFileToProject(user.uid, projectId, {
          name: file.name,
          url,
          type: file.type,
          size: file.size,
        });

        setFiles(prev => [...prev, file.name]);
        console.log(`[ProjectChat] Uploaded file: ${file.name}`);
      } catch (error) {
        console.error('[ProjectChat] File upload error:', error);
      }
    }
  };

  const handleAddLink = async (url: string) => {
    if (url.trim()) {
      const trimmedUrl = url.trim();
      setLinks(prev => [...prev, trimmedUrl]);
      setNewLink('');

      // Persist to Firestore
      if (user?.uid) {
        const projectId = project.project_id || String(project.id);
        try {
          const { addLinkToProject } = await import('../services/projectsApi');
          await addLinkToProject(user.uid, projectId, trimmedUrl);
          console.log(`[ProjectChat] Added link: ${trimmedUrl}`);
        } catch (error) {
          console.error('[ProjectChat] Error adding link:', error);
        }
      }
    }
  };

  const handleSaveSettings = () => {
    onUpdateProject({
      name: editName,
      description: editDescription,
      instructions: editInstructions,
      goal: editGoal,
      files,
      links
    });
    setShowSettings(false);
  };

  const handleConnectApp = async (app: Omit<ProjectApp, 'connected_at'>) => {
    if (!user?.uid) return;
    const projectId = project.project_id || String(project.id);

    setIsConnectingApp(true);
    setAppConnectError(null);

    try {
      await connectAppToProject(user.uid, projectId, app);
      const newApp: ProjectApp = { ...app, connected_at: new Date().toISOString() };
      setConnectedApps(prev => [...prev, newApp]);
      setAppModalOpen(false);
    } catch (error) {
      console.error('[ProjectChat] Error connecting app:', error);
      setAppConnectError(`Failed to connect ${app.name}. Please try again.`);
    } finally {
      setIsConnectingApp(false);
    }
  };

  // Available for disconnect button in app management UI (Phase 4 enhancement)
  const handleDisconnectApp = async (provider: string) => {
    if (!user?.uid) return;
    const projectId = project.project_id || String(project.id);
    try {
      await disconnectAppFromProject(user.uid, projectId, provider);
      setConnectedApps(prev => prev.filter(a => a.provider !== provider));
    } catch (error) {
      console.error('[ProjectChat] Error disconnecting app:', error);
    }
  };
  // Suppress unused warning - will be wired to UI in next iteration
  void handleDisconnectApp;

  const handleAddLinkFromMenu = () => {
    setLinkModalOpen(true);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const getModeIcon = () => {
    switch (chatMode) {
      case 'Fast': return <Zap className="w-4 h-4 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" />;
      case 'Super Think': return <Cpu className="w-4 h-4 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" />;
      default: return <Activity className="w-4 h-4 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" />;
    }
  };

  // Settings Panel
  if (showSettings) {
    return (
      <div className="fixed inset-0 z-[70] bg-black font-sans">
        <SpaceBackground />

        <div className="relative z-10 flex flex-col h-full">
          {/* Header */}
          <header className="p-4 md:p-6 flex items-center border-b border-cyan-400/10">
            <button
              onClick={() => setShowSettings(false)}
              className="p-2 md:p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-4 md:mr-6"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
            </button>

            <button onClick={handleLogoClick} className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity">
              <AtomIcon className="w-8 h-8 md:w-10 md:h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
              <h1 className="text-xl md:text-2xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans">
                Project Settings
              </h1>
            </button>

            <button onClick={handleLogoClick} className="ml-auto hover:scale-105 transition-transform hidden md:block">
              <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
            </button>
          </header>

          {/* Settings Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="max-w-3xl mx-auto space-y-5 md:space-y-6">
              {/* Name */}
              <div>
                <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Project Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none transition-all text-base"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none transition-all resize-none text-base"
                />
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Instructions</label>
                <textarea
                  value={editInstructions}
                  onChange={(e) => setEditInstructions(e.target.value)}
                  rows={5}
                  className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none transition-all resize-none text-base"
                />
              </div>

              {/* Goal */}
              <div>
                <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Goal / Purpose</label>
                <textarea
                  value={editGoal}
                  onChange={(e) => setEditGoal(e.target.value)}
                  rows={3}
                  className="w-full bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none transition-all resize-none text-base"
                />
              </div>

              {/* Knowledge */}
              <div>
                <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Knowledge</label>

                {/* Files */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {files.map((file, i) => (
                    <div key={i} className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                      <FileText className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400/80 text-sm truncate max-w-[150px]">{file}</span>
                      <button onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-cyan-400/50 hover:text-cyan-300">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={handleAddFile}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-500/[0.05] border border-dashed border-cyan-400/30 rounded-lg hover:bg-cyan-400/10 transition-all"
                  >
                    <Upload className="w-4 h-4 text-cyan-400" />
                    <span className="text-cyan-400/60 text-sm">Add file</span>
                  </button>
                </div>

                {/* Links */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {links.map((link, i) => (
                    <div key={i} className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/30 rounded-lg max-w-full">
                      <Link className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span className="text-cyan-400/80 text-sm truncate">{link}</span>
                      <button onClick={() => setLinks(links.filter((_, idx) => idx !== i))} className="text-cyan-400/50 hover:text-cyan-300 shrink-0">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newLink}
                    onChange={(e) => setNewLink(e.target.value)}
                    placeholder="Add a link..."
                    className="flex-1 bg-cyan-500/[0.05] border border-cyan-400/30 rounded-xl px-4 py-2 text-cyan-300 placeholder-cyan-400/40 focus:border-cyan-400/60 focus:outline-none text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddLink(newLink)}
                  />
                  <button
                    onClick={() => handleAddLink(newLink)}
                    className="px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 rounded-xl hover:bg-cyan-400/20 transition-all"
                  >
                    <Link className="w-4 h-4 text-cyan-400" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="p-4 md:p-6 border-t border-cyan-400/10 bg-black/50 backdrop-blur-xl">
            <div className="max-w-3xl mx-auto">
              {settingsSavedAt && (
                <p className="text-green-400/70 text-xs text-center mb-2">
                  Auto-saved at {settingsSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <button
                onClick={handleSaveSettings}
                className="w-full py-4 rounded-xl font-bold text-lg transition-all bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:shadow-[0_0_40px_rgba(34,211,238,0.7)]"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx,.txt"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    );
  }

  // Full Screen Chat View - Same style as HomeScreen
  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        {/* Header with project name */}
        <header className="absolute top-0 left-0 w-full p-4 md:p-6 flex items-center z-20">
          <button
            onClick={onClose}
            className="p-2 md:p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-4 md:mr-6"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
          </button>

          <button onClick={handleLogoClick} className="flex flex-col items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
            <h1 className="text-xl md:text-2xl lg:text-[2.5rem] font-black tracking-tighter text-cyan-400 mb-1 drop-shadow-[0_0_20px_rgba(34,211,238,0.4)] line-clamp-1 font-sans">
              {project.name}
            </h1>
            <div className="flex items-center space-x-2">
              <div className="h-[2px] w-4 bg-cyan-400 shadow-[0_0_5px_cyan]" />
              <p className="text-cyan-400 text-[9px] md:text-[10px] tracking-[0.2em] font-bold uppercase drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]">
                Project Chat
              </p>
              <div className="h-[2px] w-4 bg-cyan-400 shadow-[0_0_5px_cyan]" />
            </div>
          </button>

          <button onClick={handleLogoClick} className="ml-auto hover:scale-105 transition-transform cursor-pointer hidden md:block group">
            <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow group-hover:scale-110 transition-transform" />
          </button>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 md:space-x-3 ml-2 md:ml-4">
            <button
              onClick={() => alert('Workflows coming soon')}
              className="flex items-center space-x-2 px-3 py-2 md:px-4 md:py-2 rounded-xl bg-cyan-900/20 hover:bg-cyan-900/40 border border-cyan-400/30 hover:border-cyan-400/60 transition-all duration-300 shadow-[0_0_15px_rgba(34,211,238,0.1)] group"
            >
              <Settings className="w-4 h-4 md:w-5 md:h-5 text-cyan-400/70 group-hover:text-cyan-300" />
              <span className="text-xs md:text-sm font-semibold text-cyan-400/80 group-hover:text-cyan-300 hidden sm:block">
                Workflows
              </span>
            </button>
            <button
              onClick={() => alert('Shadow Mode coming soon')}
              className="flex items-center space-x-2 px-3 py-2 md:px-4 md:py-2 rounded-xl bg-purple-900/20 hover:bg-purple-900/40 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300 shadow-[0_0_15px_rgba(168,85,247,0.1)] group"
            >
              <Ghost className="w-4 h-4 md:w-5 md:h-5 text-purple-400/70 group-hover:text-purple-300" />
              <span className="text-xs md:text-sm font-semibold text-purple-400/80 group-hover:text-purple-300 hidden sm:block">
                Shadow Mode
              </span>
            </button>
          </div>
        </header>

        {/* Chat Area */}
        <div
          ref={chatScrollRef}
          className="flex-1 flex flex-col pb-48 md:pb-64 px-4 overflow-y-auto w-full items-center pt-24 md:pt-28"
        >
          <div className="w-full max-w-3xl pl-0 md:pl-4 lg:pl-0">
            <div className="flex flex-col items-start space-y-4 ml-0 md:ml-[7.5rem]">
              {/* Conversation */}
              {conversation.map((msg, i) => (
                <div key={i} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mr-3 shrink-0">
                      <AtomIcon className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
                    </div>
                  )}
                  <div className={`max-w-[85%] md:max-w-[70%] p-3 md:p-4 rounded-2xl ${msg.role === 'user'
                      ? 'bg-cyan-400/20 border border-cyan-400/40'
                      : 'bg-cyan-500/[0.05] border border-cyan-400/20'
                    }`}>
                    <div className="text-cyan-300 text-sm leading-relaxed whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{
                        __html: (msg.message || '')
                          .replace(/### \*\*(.+?)\*\*/g, '<div class="font-semibold text-cyan-300 mt-3 mb-1">$1</div>')
                          .replace(/### (.+)/g, '<div class="font-semibold text-cyan-300 mt-3 mb-1">$1</div>')
                          .replace(/## (.+)/g, '<div class="font-bold text-cyan-200 mt-4 mb-2 text-lg">$1</div>')
                          .replace(/^\*\*([^*]+)\*\*$/gm, '<div class="font-semibold text-cyan-300 mt-3 mb-1">$1</div>')
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-cyan-200">$1</strong>')
                          .replace(/^\* (.+)$/gm, '<div class="flex items-start gap-2 my-1 ml-3"><span class="text-cyan-400 mt-0.5 text-xs">•</span><span>$1</span></div>')
                          .replace(/^- (.+)$/gm, '<div class="flex items-start gap-2 my-1 ml-3"><span class="text-cyan-400 mt-0.5 text-xs">•</span><span>$1</span></div>')
                          .replace(/^(\d+)\. (.+)$/gm, '<div class="flex items-start gap-2 my-1 ml-3"><span class="text-cyan-400 font-bold text-xs mt-0.5">$1.</span><span>$2</span></div>')
                      }}
                    />
                  </div>
                </div>
              ))}

              {/* Streaming response or Loading indicator */}
              {isLoading && (
                <div className="flex w-full justify-start">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mr-3 shrink-0">
                    <AtomIcon className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 animate-spin" />
                  </div>
                  <div className="max-w-[85%] md:max-w-[70%] p-3 md:p-4 rounded-2xl bg-cyan-500/[0.05] border border-cyan-400/20">
                    {streamingContent ? (
                      <p className="text-cyan-300 text-sm leading-relaxed">
                        {streamingContent}
                        <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 animate-pulse" />
                      </p>
                    ) : (
                      <p className="text-cyan-400/60 text-sm">Thinking...</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Chat & Nav Section - Same as HomeScreen */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4">
          {/* Chat Bar - Same style as HomeScreen */}
          <div className="w-full max-w-5xl mb-4 md:mb-6 relative">
            <GlassCard className="flex items-center p-2 pr-3 md:pr-4 relative z-40 transition-all duration-300 ring-2 ring-cyan-400/40 hover:ring-cyan-400/60 focus-within:ring-cyan-400/80 shadow-[0_0_30px_rgba(34,211,238,0.3),0_0_60px_rgba(34,211,238,0.15)] hover:shadow-[0_0_40px_rgba(34,211,238,0.5),0_0_80px_rgba(34,211,238,0.25)] focus-within:shadow-[0_0_50px_rgba(34,211,238,0.6),0_0_100px_rgba(34,211,238,0.3)]">

              {/* Plus Button */}
              <div className="relative" ref={plusMenuRef}>
                <button
                  onClick={() => setPlusMenuOpen(!plusMenuOpen)}
                  className={`p-2 md:p-3 rounded-full mr-2 md:mr-3 transition-all duration-300 border border-cyan-400/40 ${plusMenuOpen ? 'bg-cyan-400 rotate-45 text-black shadow-[0_0_35px_rgba(34,211,238,0.8),0_0_60px_rgba(34,211,238,0.4)]' : 'bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.2)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5),0_0_50px_rgba(34,211,238,0.25)] hover:border-cyan-400/70'}`}
                >
                  <Plus className="w-5 h-5" />
                </button>

                <PlusMenu
                  isOpen={plusMenuOpen}
                  onClose={() => setPlusMenuOpen(false)}
                  onAddFile={handleAddFile}
                  onAddLink={handleAddLinkFromMenu}
                  onEditSettings={() => setShowSettings(true)}
                />
              </div>

              {/* Input Field */}
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder={`Chat with ${project.name}...`}
                className="flex-1 bg-transparent border-none outline-none text-cyan-300 placeholder-cyan-400/40 text-base md:text-lg px-2 font-medium drop-shadow-[0_0_5px_rgba(34,211,238,0.2)]"
              />

              {/* Right Side Tools */}
              <div className="flex items-center space-x-1 md:space-x-3 pl-2">

                {/* Mode Selector - Hidden on mobile */}
                <div className="relative hidden md:block" ref={modeMenuRef}>
                  <button
                    onClick={() => setModeMenuOpen(!modeMenuOpen)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-full bg-cyan-900/20 hover:bg-cyan-900/40 border border-cyan-400/40 hover:border-cyan-400/70 transition-all duration-300 shadow-[0_0_15px_rgba(34,211,238,0.15)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4),0_0_50px_rgba(34,211,238,0.2)] group"
                  >
                    {getModeIcon()}
                    <span className="text-xs font-extrabold text-cyan-400 uppercase tracking-wider group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]">{chatMode}</span>
                  </button>

                  {modeMenuOpen && (
                    <div className="absolute bottom-14 right-0 w-56 bg-black/95 backdrop-blur-2xl border border-cyan-400/30 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(34,211,238,0.2)] z-50">
                      {[
                        { name: 'Fast', desc: 'Quick search-only answers' },
                        { name: 'Super', desc: 'Comprehensive 3-lane intelligence' }
                      ].map(mode => (
                        <button
                          key={mode.name}
                          onClick={() => { setChatMode(mode.name); setModeMenuOpen(false); }}
                          className="w-full text-left px-5 py-3 hover:bg-cyan-400/10 transition-colors border-b border-cyan-400/10 last:border-0"
                        >
                          <div className="text-xs font-bold uppercase tracking-wider text-cyan-400/80 hover:text-cyan-300">{mode.name}</div>
                          <div className="text-[10px] text-cyan-400/50 mt-0.5">{mode.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-[1.5px] h-6 bg-cyan-400/20 mx-1 hidden md:block"></div>

                <button onClick={() => alert('Voice input coming soon')} className="p-2 md:p-2.5 rounded-full hover:bg-cyan-400/15 text-cyan-400/60 hover:text-cyan-300 transition-all duration-300 border border-transparent hover:border-cyan-400/50 hover:shadow-[0_0_20px_rgba(34,211,238,0.4),0_0_40px_rgba(34,211,238,0.2)] group">
                  <Mic className="w-5 h-5 group-hover:drop-shadow-[0_0_12px_rgba(34,211,238,0.9)]" />
                </button>
                <button onClick={() => navigate('/live')} className="p-2 md:p-2.5 rounded-full hover:bg-cyan-400/15 text-cyan-400/60 hover:text-cyan-300 transition-all duration-300 border border-transparent hover:border-cyan-400/50 hover:shadow-[0_0_20px_rgba(34,211,238,0.4),0_0_40px_rgba(34,211,238,0.2)] group hidden md:block">
                  <Radio className="w-5 h-5 group-hover:drop-shadow-[0_0_12px_rgba(34,211,238,0.9)]" />
                </button>
                <button onClick={() => alert('Ghost mode coming soon')} className="p-2 md:p-2.5 rounded-full hover:bg-cyan-400/15 text-cyan-400/60 hover:text-cyan-300 transition-all duration-300 border border-transparent hover:border-cyan-400/50 hover:shadow-[0_0_20px_rgba(34,211,238,0.4),0_0_40px_rgba(34,211,238,0.2)] group hidden md:block">
                  <Ghost className="w-5 h-5 group-hover:drop-shadow-[0_0_12px_rgba(34,211,238,0.9)]" />
                </button>

                <div className="w-[1.5px] h-6 bg-cyan-400/20 mx-1 hidden md:block"></div>

                {/* Send Arrow Button */}
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim()}
                  className={`p-2 md:p-2.5 rounded-full transition-all duration-300 border ${inputText.trim() ? 'bg-cyan-400 text-black border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.6),0_0_50px_rgba(34,211,238,0.3)] hover:shadow-[0_0_35px_rgba(34,211,238,0.8),0_0_70px_rgba(34,211,238,0.4)] hover:scale-105' : 'bg-cyan-400/10 text-cyan-400/40 border-cyan-400/20 cursor-not-allowed'}`}
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
              </div>
            </GlassCard>
          </div>

          <BottomNav activeTab="projects" />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }} userName={user?.displayName || user?.email?.split("@")[0]} userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />

      {/* Project Context Panel - shows apps, tasks, knowledge */}
      <ProjectContextPanel
        projectId={project.project_id || String(project.id)}
        projectName={project.name}
        projectIcon={project.icon}
        projectColor={project.color}
        apps={connectedApps.map(a => ({
          provider: a.provider,
          name: a.name,
          icon: a.icon,
          connected_at: a.connected_at
        }))}
        tasks={projectTasks.map(t => ({
          id: t.task_id || (t as unknown as { id: string }).id,
          title: t.title,
          status: t.status as string as 'running' | 'pending' | 'scheduled' | 'completed'
        }))}
        tasksCount={projectTasks.length || project.tasksCount}
        knowledge={{
          files: files,
          links: links
        }}
        onAddApp={() => setAppModalOpen(true)}
        onAddFile={handleAddFile}
        onAddLink={handleAddLinkFromMenu}
        onManageKnowledge={() => setShowSettings(true)}
        isOpen={contextPanelOpen}
        onToggle={() => setContextPanelOpen(!contextPanelOpen)}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf,.doc,.docx,.txt"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Link input modal */}
      <LinkInputModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onAdd={handleAddLink}
      />

      {/* Connect App Modal */}
      {appModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-black/95 border border-cyan-400/30 rounded-2xl w-full max-w-md max-h-[70vh] overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)]">
            {/* Header */}
            <div className="p-4 border-b border-cyan-400/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-cyan-300">Connect App</h3>
              <button
                onClick={() => { setAppModalOpen(false); setAppConnectError(null); }}
                className="p-2 rounded-full hover:bg-cyan-400/10 transition-colors"
                disabled={isConnectingApp}
              >
                <X className="w-5 h-5 text-cyan-400/60" />
              </button>
            </div>

            {/* Error message */}
            {appConnectError && (
              <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 text-sm">{appConnectError}</p>
              </div>
            )}

            {/* Loading indicator */}
            {isConnectingApp && (
              <div className="p-4 flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                <span className="text-cyan-400/70 text-sm">Connecting...</span>
              </div>
            )}

            {/* App List */}
            <div className={`p-4 overflow-y-auto max-h-[50vh] space-y-2 ${isConnectingApp ? 'opacity-50 pointer-events-none' : ''}`}>
              {AVAILABLE_APPS.filter(app => !connectedApps.some(c => c.provider === app.provider)).map(app => (
                <button
                  key={app.provider}
                  onClick={() => handleConnectApp(app)}
                  disabled={isConnectingApp}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-cyan-400/5 hover:bg-cyan-400/10 border border-cyan-400/10 hover:border-cyan-400/30 transition-all disabled:opacity-50"
                >
                  <span className="text-xl">{app.icon}</span>
                  <span className="text-cyan-300 font-medium">{app.name}</span>
                </button>
              ))}
              {AVAILABLE_APPS.filter(app => !connectedApps.some(c => c.provider === app.provider)).length === 0 && (
                <p className="text-cyan-400/50 text-center py-8">All available apps are connected</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Import projects API for Firestore persistence
import {
  getUserProjects,
  createProject as createProjectApi,
  updateProject as updateProjectApi,
  deleteProject as deleteProjectApi,
  connectAppToProject,
  disconnectAppFromProject,
  AVAILABLE_APPS,
  PROJECT_TEMPLATES,
  ProjectTemplate,
  Project as ApiProject,
  ProjectApp
} from '../services/projectsApi';

// updateProjectApi is now wired in handleUpdateProject

// Map API project to UI project format
const mapApiToUiProject = (apiProject: ApiProject): Project => ({
  id: parseInt(apiProject.id.replace(/\D/g, '')) || Date.now(),
  project_id: apiProject.id,
  name: apiProject.name,
  icon: apiProject.icon,
  color: apiProject.color,
  tasksCount: apiProject.tasks_count || 0,
  description: apiProject.description,
  instructions: apiProject.instructions,
  goal: apiProject.goal,
  files: apiProject.files,
  links: apiProject.links,
  connected_apps: apiProject.connected_apps
});

// Extended Project interface to include API id
interface Project {
  id: number;
  project_id?: string;
  name: string;
  icon: string;
  color: string;
  tasksCount: number;
  description?: string;
  instructions?: string;
  goal?: string;
  files?: string[];
  links?: string[];
  connected_apps?: ProjectApp[];
}

export default function ProjectsScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [showNewProject, setShowNewProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [menuProject, setMenuProject] = useState<Project | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Load projects from Firestore
  const loadProjects = useCallback(async () => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const apiProjects = await getUserProjects(user.uid);
      const uiProjects = apiProjects.map(mapApiToUiProject);
      setProjects(uiProjects);
    } catch (error) {
      console.error('[ProjectsScreen] Error loading projects:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Refresh project list when page becomes visible (e.g., returning from project creation)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadProjects();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadProjects]);

  const handleCreateProject = async (newProject: Partial<Project>, tasksList?: Task[], appsList?: ProjectApp[], pendingFileUploads?: File[]) => {
    if (!user?.uid) return;

    try {
      // Create in Firestore
      const apiProject = await createProjectApi(user.uid, {
        name: newProject.name || 'Untitled Project',
        icon: newProject.icon || '📁',
        color: newProject.color || 'from-cyan-400 to-blue-400',
        description: newProject.description,
        instructions: newProject.instructions,
        goal: newProject.goal,
        files: newProject.files,
        links: newProject.links
      });

      const projectId = apiProject.id;

      // Upload pending files to Firebase Storage and persist metadata
      if (pendingFileUploads && pendingFileUploads.length > 0) {
        try {
          const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const { storage } = await import('../services/firebase');
          const { addFileToProject } = await import('../services/projectsApi');

          for (const file of pendingFileUploads) {
            const storagePath = `projects/${user.uid}/${projectId}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            await addFileToProject(user.uid, projectId, {
              name: file.name,
              url,
              type: file.type,
              size: file.size,
            });
          }
          console.log(`[ProjectsScreen] Uploaded ${pendingFileUploads.length} files for new project`);
        } catch (uploadError) {
          console.error('[ProjectsScreen] Error uploading files for new project:', uploadError);
        }
      }

      // Associate connected tasks with the project
      if (tasksList && tasksList.length > 0) {
        await Promise.allSettled(
          tasksList.map(task =>
            updateTask({ user_id: user.uid, task_id: task.task_id, project_id: projectId })
          )
        );
      }

      // Connect apps to the project (integration apps beyond what was passed in creation)
      if (appsList && appsList.length > 0) {
        await Promise.allSettled(
          appsList.map(app =>
            connectAppToProject(user.uid, projectId, { provider: app.provider, name: app.name, icon: app.icon })
          )
        );
      }

      const project = mapApiToUiProject(apiProject);
      setProjects([project, ...projects]);
      setShowNewProject(false);
      // Immediately open the project chat view
      setSelectedProject(project);
    } catch (error) {
      console.error('[ProjectsScreen] Error creating project:', error);
      alert('Failed to create project. Please try again.');
    }
  };

  const handleUpdateProject = async (updates: Partial<Project>) => {
    if (!selectedProject || !user?.uid) return;

    // Update local state immediately for responsiveness
    const updatedProject = { ...selectedProject, ...updates };
    setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
    setSelectedProject(updatedProject);

    // Persist to backend if we have an API id
    if (selectedProject.project_id) {
      try {
        await updateProjectApi(user.uid, selectedProject.project_id, {
          name: updates.name,
          description: updates.description,
          goal: updates.goal,
          instructions: updates.instructions,
          files: updates.files,
          links: updates.links,
        });
      } catch (error) {
        console.error('[ProjectsScreen] Error updating project:', error);
        // Revert local state on failure
        setProjects(projects.map(p => p.id === selectedProject.id ? selectedProject : p));
        setSelectedProject(selectedProject);
        alert('Failed to save project changes. Please try again.');
      }
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    if (!user?.uid) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Confirmation dialog — never delete without asking
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;

    try {
      // Delete from Firestore if we have an API id
      if (project.project_id) {
        await deleteProjectApi(user.uid, project.project_id);
      }

      // Update local state
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('[ProjectsScreen] Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  const handleUpdateProjectFromMenu = (projectId: number) => {
    // Open the project in edit/update mode (navigates to project settings)
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setSelectedProject(project);
    }
  };

  // Show new project form
  if (showNewProject) {
    return (
      <NewProjectForm
        onClose={() => setShowNewProject(false)}
        onCreate={handleCreateProject}
      />
    );
  }

  // Show project chat view
  if (selectedProject) {
    return (
      <ProjectChatView
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onUpdateProject={handleUpdateProject}
      />
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Content Area with scroll fade - matching MyAppsScreen */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex flex-col justify-start pt-24 md:pt-28 px-4 overflow-y-auto w-full items-center"
          style={{
            paddingBottom: 240,
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))'
          }}
        >
          <div className="w-full max-w-3xl">
            <PageTitle title="Projects" />

            {/* Projects Section - matching MyAppsScreen style with responsive grid */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest flex items-center space-x-2">
                  <Briefcase className="w-4 h-4" />
                  <span>My Projects ({isLoading ? '...' : projects.length})</span>
                </h3>
              </div>

              {/* Responsive grid: 2 cols on mobile, 3 on md, 4 on lg */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                <CreateProjectButton onClick={() => setShowNewProject(true)} />
                {isLoading ? (
                  <div className="col-span-full flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                  </div>
                ) : (
                  projects.map(project => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onClick={() => setSelectedProject(project)}
                      onMenuOpen={() => setMenuProject(project)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - matching MyAppsScreen */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={() => {
              if (inputText.trim()) {
                navigate(`/?message=${encodeURIComponent(inputText.trim())}`);
                setInputText('');
              }
            }}
          />
          <BottomNav activeTab="projects" />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }} userName={user?.displayName || user?.email?.split("@")[0]} userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />

      {/* Action Modal */}
      <ActionModal
        isOpen={!!menuProject}
        onClose={() => setMenuProject(null)}
        project={menuProject}
        onUpdateProject={() => menuProject && handleUpdateProjectFromMenu(menuProject.id)}
        onDelete={() => menuProject && handleDeleteProject(menuProject.id)}
      />
    </div>
  );
}
